import { IResortTrailmapPage } from '@/api/resort/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import {
  formatDateFromStr,
  SHORT_MONTH_WITH_YEAR_DATE_FORMAT,
} from '@/util/formatDate';
import { SnowUnit } from '@/components/blocks/Unit';
import { SectionHeader } from '../SectionHeader';
import { MetricListItem } from '../MetricListItem';

interface Props {
  trailmap: IResortTrailmapPage;
  mountainOverview: { dangerouslySetInnerHTML: { __html: string; }; }
  isPermanentlyClosed: boolean;
  annualSnowfall?: number;
}

// Important Dates list — same row treatment used by TerrainOverview +
// TotalLifts so all three "labelled stat row" surfaces on the Overview
// page share the same primitive.

const headingClass = 'mb-3 mt-8 font-heading text-xl text-secondary md:text-2xl';

export const WhenToGoSection = ({
  trailmap, mountainOverview, isPermanentlyClosed, annualSnowfall,
}: Props) => {
  const { t } = useOTSTranslation('common');
  const getDatePart = (date?: string) => (date ? date.split('T')[0] : date);
  const projectedOpening = getDatePart(trailmap.dates.projectedOpening);
  const projectedClosing = getDatePart(trailmap.dates.projectedClosing);
  const na = t('common.na');

  const formatProjectedDate = (d?: string) => {
    if (isPermanentlyClosed) return na;
    if (!d) return null;
    return formatDateFromStr(d, SHORT_MONTH_WITH_YEAR_DATE_FORMAT);
  };

  const rows: Array<{ title: string; metric: React.ReactNode }> = [];

  if (projectedOpening) {
    rows.push({
      title: t('region.openDates.projectedDate'),
      metric: formatProjectedDate(projectedOpening),
    });
  }
  if (projectedClosing) {
    rows.push({
      title: t('region.closingDates.projectedDate'),
      metric: formatProjectedDate(projectedClosing),
    });
  }
  if (trailmap.dates.projectedDaysOpen) {
    rows.push({
      title: t('resort.overview.projectedDaysOpen'),
      metric: trailmap.dates.projectedDaysOpen,
    });
  }
  if (trailmap.dates.daysOpenLastYear) {
    rows.push({
      title: t('resort.overview.daysOpenLastYear'),
      metric: trailmap.dates.daysOpenLastYear,
    });
  }
  if (trailmap.dates.yearsOpen) {
    rows.push({
      title: t('resort.overview.yearsOpen'),
      metric: trailmap.dates.yearsOpen,
    });
  }
  if (annualSnowfall) {
    rows.push({
      title: t('resort.overview.averageSnowfall'),
      metric: <SnowUnit values={annualSnowfall} />,
    });
  }

  return (
    <section>
      <h3 className={headingClass}>{t('resort.overview.importantDates')}</h3>
      {!!rows.length && (
        <ul>
          {rows.map((row) => (
            <MetricListItem
              as="li"
              variant="row"
              key={row.title}
              title={row.title}
              metric={row.metric}
            />
          ))}
        </ul>
      )}
      <SectionHeader description={mountainOverview} />
    </section>
  );
};
